import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Chip,
  Grid,
  InputAdornment
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function SupportChats() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChats();

    // Poll for new chats/messages every 10 seconds
    pollIntervalRef.current = setInterval(() => {
      loadChats();
      if (selectedChat) {
        pollNewMessages();
      }
    }, 10000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedChat]);

  const loadChats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/support/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setChats(res.data.chats || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading chats:', error);
      setLoading(false);
    }
  };

  const loadChatMessages = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/support/chats/${chatId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSelectedChat(res.data.chat);
      setMessages(res.data.messages || []);
      markChatAsRead(chatId);
    } catch (error) {
      console.error('Error loading chat messages:', error);
    }
  };

  const pollNewMessages = async () => {
    if (!selectedChat || messages.length === 0) return;

    try {
      const lastMessage = messages[messages.length - 1];
      const lastTimestamp = new Date(lastMessage.createdAt).getTime();

      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/support/chats/${selectedChat.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newMessages = res.data.messages.filter(m => 
        new Date(m.createdAt).getTime() > lastTimestamp
      );

      if (newMessages.length > 0) {
        setMessages(res.data.messages);
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  };

  const markChatAsRead = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/support/chats/${chatId}/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update chat in list
      setChats(prev => prev.map(chat => 
        chat.id === chatId ? { ...chat, unreadByAdmin: 0 } : chat
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async () => {
    if ((!inputText.trim() && attachments.length === 0) || sending || !selectedChat) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/support/chats/${selectedChat.id}/messages`,
        {
          text: inputText.trim(),
          attachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, res.data.message]);
      setInputText('');
      setAttachments([]);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('Файл слишком большой.\n\nМаксимальный размер: 10 МБ\nРазмер вашего файла: ' + (file.size / 1024 / 1024).toFixed(2) + ' МБ');
      e.target.value = '';
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Неверный формат файла.\n\nРазрешены только:\n- JPG/JPEG изображения\n- PNG изображения\n- GIF анимации\n- PDF документы');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API_BASE}/support/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttachments(prev => [...prev, res.data]);
      e.target.value = ''; // Clear input
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Не удалось загрузить файл';
      alert(errorMsg);
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return d.toLocaleDateString('ru-RU');
    }
  };

  const formatLastMessageTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    
    if (diff < 60000) return 'только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ч назад`;
    return d.toLocaleDateString('ru-RU');
  };

  const renderAttachment = (attachment) => {
    const baseUrl = API_BASE.replace('/api', '');

    return (
      <Chip
        label={attachment.filename}
        component="a"
        href={`${baseUrl}${attachment.url}`}
        target="_blank"
        clickable
        sx={{ mt: 1 }}
      />
    );
  };

  const filteredChats = chats
    .filter(chat => {
      if (!searchQuery) return true;
      const user = chat.user || {};
      const searchLower = searchQuery.toLowerCase();
      return (
        (user.fullName && user.fullName.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      // Сортировка: непрочитанные сверху, потом по времени последнего сообщения
      if (a.unreadByAdmin && !b.unreadByAdmin) return -1;
      if (!a.unreadByAdmin && b.unreadByAdmin) return 1;
      return new Date(b.lastMessageAt) - new Date(a.lastMessageAt);
    });

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
        Чаты техподдержки
      </Typography>

      <Paper
        elevation={0}
        sx={{
          height: 'calc(100vh - 220px)',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {/* Chat List */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              borderRight: { md: '1px solid rgba(0, 0, 0, 0.08)' },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Поиск по имени или email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {filteredChats.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {searchQuery ? 'Чаты не найдены' : 'Нет чатов'}
                  </Typography>
                </Box>
              ) : (
                filteredChats.map((chat) => {
                  const user = chat.user || {};
                  const isSelected = selectedChat?.id === chat.id;

                  return (
                    <ListItem
                      key={chat.id}
                      button
                      selected={isSelected}
                      onClick={() => loadChatMessages(chat.id)}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(9, 132, 227, 0.08)',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={chat.unreadByAdmin || 0}
                          color="error"
                          invisible={!chat.unreadByAdmin}
                        >
                          <Avatar
                            src={user.avatarUrl}
                            sx={{ bgcolor: '#74b9ff' }}
                          >
                            {user.fullName ? user.fullName[0].toUpperCase() : '?'}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography fontWeight={chat.unreadByAdmin ? 600 : 400}>
                              {user.fullName || user.email || 'Неизвестный пользователь'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.role === 'client' ? 'Заказчик' : 'Респондент'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatLastMessageTime(chat.lastMessageAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </Grid>

          {/* Chat Messages */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!selectedChat ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography>Выберите чат для просмотра сообщений</Typography>
              </Box>
            ) : (
              <>
                {/* Chat Header */}
                <Box
                  sx={{
                    p: 2,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Avatar
                    src={selectedChat.user?.avatarUrl}
                    sx={{ bgcolor: '#74b9ff' }}
                  >
                    {selectedChat.user?.fullName
                      ? selectedChat.user.fullName[0].toUpperCase()
                      : '?'}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={600}>
                      {selectedChat.user?.fullName || selectedChat.user?.email || 'Неизвестный'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedChat.user?.email}
                    </Typography>
                  </Box>
                </Box>

                {/* Messages */}
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: 2,
                    background: '#f7fafc',
                  }}
                >
                  {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                    <Box key={date}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                        <Chip label={date} size="small" sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
                      </Box>

                      {dateMessages.map((message, idx) => {
                        const isSupport = message.senderRole === 'admin';

                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              justifyContent: isSupport ? 'flex-end' : 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '70%',
                                display: 'flex',
                                flexDirection: isSupport ? 'row-reverse' : 'row',
                                gap: 1,
                                alignItems: 'flex-end',
                              }}
                            >
                              {!isSupport && (
                                <Avatar
                                  src={selectedChat.user?.avatarUrl}
                                  sx={{ width: 32, height: 32, bgcolor: '#cbd5e0' }}
                                >
                                  {selectedChat.user?.fullName
                                    ? selectedChat.user.fullName[0].toUpperCase()
                                    : '?'}
                                </Avatar>
                              )}

                              <Box>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 1.5,
                                    borderRadius: isSupport
                                      ? '16px 16px 4px 16px'
                                      : '16px 16px 16px 4px',
                                    background: isSupport
                                      ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                                      : '#d1d5db',
                                    color: isSupport ? '#fff' : '#2d3748',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                  }}
                                >
                                  {isSupport && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        opacity: 0.9,
                                        display: 'block',
                                        mb: 0.5,
                                      }}
                                    >
                                      Техподдержка ThinkLink
                                    </Typography>
                                  )}

                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {message.text}
                                  </Typography>

                                  {message.attachments && message.attachments.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      {message.attachments.map((att, attIdx) => (
                                        <Box key={attIdx}>{renderAttachment(att)}</Box>
                                      ))}
                                    </Box>
                                  )}
                                </Paper>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#a0aec0',
                                    display: 'block',
                                    mt: 0.5,
                                    textAlign: isSupport ? 'right' : 'left',
                                  }}
                                >
                                  {formatTime(message.createdAt)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}

                  <div ref={messagesEndRef} />
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, background: '#fff', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  {attachments.length > 0 && (
                    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {attachments.map((att, idx) => (
                        <Chip
                          key={idx}
                          label={att.filename}
                          onDelete={() => removeAttachment(idx)}
                          size="small"
                          sx={{ maxWidth: 200 }}
                        />
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />

                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || sending}
                      sx={{ color: '#0984e3' }}
                    >
                      {uploading ? <CircularProgress size={24} /> : <AttachFileIcon />}
                    </IconButton>

                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      placeholder="Напишите сообщение от имени техподдержки..."
                      disabled={sending}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '16px',
                        },
                      }}
                    />

                    <IconButton
                      onClick={handleSend}
                      disabled={(!inputText.trim() && attachments.length === 0) || sending}
                      sx={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: '#fff',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                        },
                        '&.Mui-disabled': {
                          background: '#cbd5e0',
                          color: '#fff',
                        },
                      }}
                    >
                      {sending ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
